import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupOption } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import type { FeedbackPayload } from '@/lib/give-ask-api';

interface FulfilAskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function FulfilAskDialog({ open, onOpenChange, onConfirm }: FulfilAskDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark this ask as fulfilled?</AlertDialogTitle>
          <AlertDialogDescription>
            It will leave the open list and can no longer be replied to. You will be asked one quick question next.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Mark fulfilled</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface FulfilFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: FeedbackPayload) => Promise<void>;
  onSkip: () => void;
}

const OPTIONS: { value: FeedbackPayload['helpedByPlatform']; label: string }[] = [
  { value: 'YES', label: 'Yes, the platform helped' },
  { value: 'MAYBE', label: 'Maybe, partly' },
  { value: 'NO', label: 'No, it did not help' },
];

export function FulfilFeedbackDialog({ open, onOpenChange, onSubmit, onSkip }: FulfilFeedbackDialogProps) {
  const [helped, setHelped] = useState<FeedbackPayload['helpedByPlatform'] | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!helped) return;
    setSubmitting(true);
    try {
      await onSubmit({ helpedByPlatform: helped, feedback: feedback.trim() || null });
      setHelped(null);
      setFeedback('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Did this platform help you fulfil the ask?</DialogTitle>
          <DialogDescription>Your answer helps us improve. You can skip this.</DialogDescription>
        </DialogHeader>
        <RadioGroup value={helped ?? ''} onValueChange={(value) => setHelped(value as FeedbackPayload['helpedByPlatform'])}>
          {OPTIONS.map((option) => (
            <RadioGroupOption key={option.value} value={option.value} label={option.label} />
          ))}
        </RadioGroup>
        <Textarea
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder="Anything you want to add (optional)"
          maxLength={1000}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onSkip}>
            Skip
          </Button>
          <Button onClick={submit} disabled={!helped || submitting}>
            {submitting ? 'Saving…' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
